import React, { useState, useEffect } from 'react';
import { Search, User, Book, DollarSign, Calendar, TrendingUp, TrendingDown, Download } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StaffLedger = () => {
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    from: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10), // Jan 1 current year
    to: new Date().toISOString().slice(0, 10) // Today
  });
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    totalDeductions: 0,
    netPaid: 0
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      fetchLedger();
    }
  }, [selectedStaff, dateFilter]);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchLedger = async () => {
    if (!selectedStaff) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch salary payments for this staff
      const paymentsResponse = await axios.get(
        `${API_URL}/api/salary/staff/${selectedStaff.id}/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const payments = paymentsResponse.data || [];
      
      // Also get salary template for context
      let salaryTemplate = null;
      try {
        const templateResponse = await axios.get(
          `${API_URL}/api/salary/templates/${selectedStaff.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        salaryTemplate = templateResponse.data;
      } catch (err) {
        // No template
      }

      // Transform payments to ledger entries
      const ledgerEntries = payments.map(payment => ({
        id: payment.id,
        date: payment.paid_at || payment.created_at,
        type: 'Salary Payment',
        description: `Salary for ${payment.month}`,
        month: payment.month,
        credit: payment.amount || 0,
        debit: 0,
        method: payment.payment_method,
        reference: payment.reference,
        balance: 0 // Will calculate
      }));

      // Sort by date
      ledgerEntries.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate running balance
      let runningBalance = 0;
      ledgerEntries.forEach(entry => {
        runningBalance += entry.credit - entry.debit;
        entry.balance = runningBalance;
      });

      // Calculate summary
      const totalEarnings = ledgerEntries.reduce((sum, e) => sum + e.credit, 0);
      const totalDeductions = ledgerEntries.reduce((sum, e) => sum + e.debit, 0);

      setLedgerData(ledgerEntries.reverse()); // Most recent first
      setSummary({
        totalEarnings,
        totalDeductions,
        netPaid: totalEarnings - totalDeductions,
        salaryTemplate
      });

    } catch (error) {
      console.error('Error fetching ledger:', error);
      setLedgerData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = staff.filter(s =>
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const exportLedger = () => {
    if (!ledgerData.length || !selectedStaff) return;

    const headers = ['Date', 'Type', 'Description', 'Credit', 'Debit', 'Balance', 'Payment Method', 'Reference'];
    const rows = ledgerData.map(entry => [
      formatDate(entry.date),
      entry.type,
      entry.description,
      entry.credit,
      entry.debit,
      entry.balance,
      entry.method || '',
      entry.reference || ''
    ]);

    const csvContent = [
      `Staff Ledger - ${selectedStaff.full_name} (${selectedStaff.employee_id})`,
      `Period: ${dateFilter.from} to ${dateFilter.to}`,
      '',
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff_ledger_${selectedStaff.employee_id}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">View staff payment and transaction history</p>
        </div>
        {selectedStaff && ledgerData.length > 0 && (
          <button 
            onClick={exportLedger}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export Ledger
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Staff List */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Select Staff</h3>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or ID..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredStaff.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedStaff(member)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedStaff?.id === member.id 
                    ? 'bg-blue-100 border-l-4 border-blue-600' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{member.full_name}</p>
                    <p className="text-xs text-gray-500">{member.employee_id}</p>
                  </div>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {member.staff_type || member.designation}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">{member.department}</p>
              </div>
            ))}
            {filteredStaff.length === 0 && (
              <p className="text-center text-gray-500 py-4">No staff found</p>
            )}
          </div>
        </div>

        {/* Ledger View */}
        <div className="lg:col-span-2 space-y-4">
          {selectedStaff ? (
            <>
              {/* Staff Info Card */}
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-7 w-7 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{selectedStaff.full_name}</h3>
                      <p className="text-sm text-gray-500">
                        {selectedStaff.employee_id} | {selectedStaff.department} | {selectedStaff.designation}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {summary.salaryTemplate && (
                      <p className="text-sm text-gray-500">Monthly Gross: {formatCurrency(summary.salaryTemplate.gross_salary)}</p>
                    )}
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.netPaid)}</p>
                    <p className="text-xs text-gray-400">Total Paid</p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm">Total Earnings</span>
                  </div>
                  <p className="text-xl font-bold">{formatCurrency(summary.totalEarnings)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <TrendingDown className="h-4 w-4" />
                    <span className="text-sm">Deductions</span>
                  </div>
                  <p className="text-xl font-bold">{formatCurrency(summary.totalDeductions)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Net Paid</span>
                  </div>
                  <p className="text-xl font-bold">{formatCurrency(summary.netPaid)}</p>
                </div>
              </div>

              {/* Ledger Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                  <h4 className="font-semibold">Transaction History</h4>
                  <span className="text-sm text-gray-500">{ledgerData.length} transactions</span>
                </div>
                
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading ledger...</p>
                  </div>
                ) : ledgerData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ledgerData.map((entry, index) => (
                          <tr key={entry.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 text-sm">
                              {formatDate(entry.date)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{entry.type}</div>
                              <div className="text-xs text-gray-500">{entry.description}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {entry.method || '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {entry.credit > 0 && (
                                <span className="text-green-600 font-medium">
                                  +{formatCurrency(entry.credit)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {entry.debit > 0 && (
                                <span className="text-red-600 font-medium">
                                  -{formatCurrency(entry.debit)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {formatCurrency(entry.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No transactions found</p>
                    <p className="text-sm text-gray-400 mt-1">Salary payments will appear here after processing payroll</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white p-12 rounded-lg shadow text-center">
              <Book className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Select a staff member to view their ledger</p>
              <p className="text-sm text-gray-400 mt-2">You can search by name or employee ID</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffLedger;
